import React, { useEffect, useState } from "react";
import { UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import useOnUserDataLoaded from "@hooks/useOnUserDataLoaded";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { redirectTo } from "@util/BrowserUtil";
import { ViewRoom } from "../../../../features/room/AmplitudeRoomsEvents";
import { RoomControls } from "../../../../features/room/RoomController";
import {
    getAllPersonalRoomNames,
    getLastVisitedRoomsDisplayNames,
    listenToRoomName,
    roomNameRef,
} from "../../../../features/room/RoomService";
import { RoomDisplayInfoList, RoomMetadata } from "../../../../features/room/RoomTypes";
import RoomSettingsModal from "../../../../features/room/components/RoomSettingsModal";
import "./CompanyButton.scss";

const DUMMY_ROOM_NAME = "My Room";
const LAST_VISITED_ROOMS_LABEL = "👈 Last visited:";
const PERSONAL_ROOMS_LABEL = "🏡 Your rooms:";
const ROOMS_SETTINGS_LABEL = "⚙️ Room Settings";

interface RoomSelectorDropdownProps {
    roomId?: string;
    roomController?: RoomControls;
}
export default function RoomSelectorDropdown(props: RoomSelectorDropdownProps) {
    const { user, userData } = useUserContext();
    const amplitudeService = AmplitudeService.getInstance();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();

    const { roomId, roomController } = { ...props };
    const roomOwnedByUser = roomId && isRoomOwnedByUser(roomId, userData);

    const [lastVisitedRoomInfo, setLastVisitedRoomInfo] = useState<RoomDisplayInfoList | undefined>(undefined);
    const [personalRoomsInfo, setPersonalRoomsInfo] = useState<RoomDisplayInfoList | undefined>(undefined);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);

    useEffect(() => {
        if (user?.uid && !lastVisitedRoomInfo) {
            getLastVisitedRoomsDisplayNames(user.uid).then((res) => {
                setLastVisitedRoomInfo(res);
            });
        }
    }, [user]);

    useOnUserDataLoaded((loadedUserData: UserEntity) => {
        if (loadedUserData && !personalRoomsInfo) {
            getAllPersonalRoomNames(loadedUserData).then((res) => {
                setPersonalRoomsInfo(res);
            });
        }
    });

    const [currRoomName, setCurrRoomName] = useState(DUMMY_ROOM_NAME);
    useEffect(() => {
        if (roomId && user) {
            const nameRef = roomNameRef(roomId);
            const listener = listenToRoomName(roomId, (name: string) => setCurrRoomName(name));

            return () => {
                nameRef.off("value", listener);
            };
        }
        return undefined;
    }, [roomId, user]);

    const handleEnterRoom = (roomId: string, isPersonalRoom: boolean) => {
        amplitudeService.logEvent(new ViewRoom({ roomId, isPersonalRoom, source: "home_menu" }));
        redirectTo(`/${Routes.Room}/${roomId}`);
    };

    const getDropdownItemsForRoom = (roomInfo: RoomMetadata, isPersonalRoom: boolean): React.ReactNode => {
        return (
            <DropdownMenuComponents.Item
                key={`home-dropdown-item-${roomInfo.id}`}
                onClick={() => handleEnterRoom(roomInfo.id, isPersonalRoom)}
            >
                {roomInfo.name}
            </DropdownMenuComponents.Item>
        );
    };

    const personalRoomItems = personalRoomsInfo && (
        <>
            <DropdownMenuComponents.Separator bold={false} />
            <DropdownMenuComponents.HeaderItem label={PERSONAL_ROOMS_LABEL} />
            {personalRoomsInfo.map((roomInfo: RoomMetadata) => getDropdownItemsForRoom(roomInfo, true))}
        </>
    );

    const lastVisitedRoomItems = lastVisitedRoomInfo && lastVisitedRoomInfo.length > 0 && (
        <>
            <DropdownMenuComponents.Separator bold={false} />
            <DropdownMenuComponents.HeaderItem label={LAST_VISITED_ROOMS_LABEL} />
            {lastVisitedRoomInfo.map((roomInfo: RoomMetadata) => getDropdownItemsForRoom(roomInfo, false))}
        </>
    );

    const handleOpenSettings = () => {
        if (user && userData && roomController && roomId) {
            if (roomOwnedByUser) {
                setIsSettingModalOpen(true);
            }
        } else {
            setSignInView(SignUpCTASource.ROOMS);
        }
    };

    const roomSettings = (
        <>
            <DropdownMenuComponents.Separator bold={false} />
            <DropdownMenuComponents.Item key={`room-settings`} onClick={handleOpenSettings}>
                {ROOMS_SETTINGS_LABEL}
            </DropdownMenuComponents.Item>
        </>
    );

    return (
        <>
            <DropdownMenuComponents.Root onOpenChange={(isOpen) => setIsDropdownOpen(isOpen)}>
                <DropdownMenuComponents.Trigger>
                    <LifeAtButtonV2 size={"small"}>
                        {currRoomName ?? DUMMY_ROOM_NAME}
                        <CaretIconToggle showOpen={isDropdownOpen} />
                    </LifeAtButtonV2>
                </DropdownMenuComponents.Trigger>
                <DropdownMenuComponents.Content>
                    {personalRoomItems}
                    {lastVisitedRoomItems}
                    {roomSettings}
                </DropdownMenuComponents.Content>
            </DropdownMenuComponents.Root>

            {roomOwnedByUser && roomController && roomId && (
                <RoomSettingsModal
                    roomControls={roomController}
                    roomId={roomId}
                    isOpen={isSettingModalOpen}
                    handleClose={() => setIsSettingModalOpen(false)}
                />
            )}
        </>
    );
}
